package com.neurobs.ccabserver.transcriptreview.states

import com.neurobs.ccab.casr.CasrDatabaseJSON.{EngineTranscriptJSON, EngineWordJSON, TestTranscriptRecordingJSON}
import com.neurobs.util.Interval
import org.scalajs.dom.{AudioBuffer, AudioContext}
import com.neurobs.util.TimeSeconds
import com.neurobs.util.TimeSeconds._

import scala.math.Ordering.Implicits
import scala.scalajs.js.typedarray.Float32Array

/** Audio data that originated from a larger recording
 *
 * @param data The audio data for the segment only
 * @param startTime Start time of the segment within the larger recording
 * @param endTime End time of the segment within the larger recording
 */
case class RecordingSegment(data: AudioBuffer, interval: TimeSecondsInterval)

object RecordingSegment {

	val silenceThreshold = 0.2
	val wordMargin: TimeSeconds = TimeSeconds(0.3)

	private def transcriptRange(t: EngineTranscriptJSON): Option[TimeSecondsInterval] = {
		// Note - must disregard empty alignment entries
		//println(s"Engine ${t.eid}")
		t.ws.find(_.w.nonEmpty) flatMap { first =>
			t.ws.foldLeft(Option.empty[EngineWordJSON]) { (acc, cur) => if (cur.w.nonEmpty) Some(cur) else acc } map { last =>
				//println(s"first '${first.w}' ${first.startTime()}")
				//println(s"last '${last.w}' ${last.endTime()}")
				Interval[TimeSeconds](first.startTime(), last.endTime())
			}
		}
	}

	private def transcriptRange(t: TestTranscriptRecordingJSON): Option[TimeSecondsInterval] = {
		val ranges = t.ts.map(transcriptRange).filter(_ != None).map(_.get)

		if (ranges.isEmpty) None
		else Some(ranges.foldLeft(ranges.head)(Interval.enclosure))
	}

	private def subBuffer(buffer: AudioBuffer, startFrame: Int, endFrame: Int)(implicit context: AudioContext): AudioBuffer = {

		val sampleRate = buffer.sampleRate.toInt
		val channels = buffer.numberOfChannels
		val frameCount = endFrame - startFrame

		val newBuffer = context.createBuffer(numOfChannels = channels, length = frameCount, sampleRate = sampleRate)
		val temp = new Float32Array(frameCount)

		for (channel <- 0 until channels) {
			buffer.copyFromChannel(destination = temp, channelNumber = channel, startInChannel = startFrame)
			newBuffer.copyToChannel(source = temp, channelNumber = channel, startInChannel = 0)
		}

		newBuffer
	}

	/** Creates segment with leading and trailing silence removed
	 *
	 * @param recording The recording
	 * @param transcript Transcript for the recording
	 * @return Segment
	 *
	 *   Any "silence" from the beginning and end of the recording is excluded up to
	 *   wordMargin seconds from the start of the 1st transcript word or end of the last trnascript
	 *   word. "silence" is defined as amplitude below silenceThreshold * maximum amplitude in the
	 *   entire recording. If no words are in the transcript, the entire recording is returned.
	 */
	def trimmed(recording: AudioBuffer, transcript: TestTranscriptRecordingJSON)(implicit context: AudioContext): RecordingSegment = {
		val recordingRange = Interval[TimeSeconds](begin = TimeSeconds(0), end = TimeSeconds(recording.duration))
		println(s"=============================================================================")
		println(s"recordingRange = $recordingRange")
		transcriptRange(transcript) match {
			case None =>
				println("no transcript")
				RecordingSegment(recording, recordingRange)
			case Some(temp) =>
				println(s"temp = $temp")
				val adjusted = Interval[TimeSeconds](begin = temp.begin - wordMargin, end = temp.end + wordMargin)
				println(s"adjusted = $adjusted")
				val valid = Interval.intersection(recordingRange, adjusted)
				println(s"valid = $valid")
				if (valid.empty) RecordingSegment(recording, recordingRange)
				else {
					val channel1 = recording.getChannelData(channel = 0)
					val max = Math.abs(channel1.max[Float])
					println(s"max = $max")
					val threshold = max * silenceThreshold
					println(s"threshold = $threshold")

					val maxBeginningSample = Math.round(valid.begin.seconds() * recording.sampleRate).toInt
					println(s"maxBeginningSample = $maxBeginningSample")
					var beginSample: Int = 0
					while ((beginSample < maxBeginningSample) && (Math.abs(channel1.get(beginSample)) < threshold)) beginSample += 1
					println(s"beginSample = $beginSample")

					val minEndingSample = Math.round(valid.end.seconds() * recording.sampleRate).toInt
					println(s"minEndingSample = $minEndingSample")
					var endSample: Int = channel1.length - 1
					while ((endSample > minEndingSample) && (Math.abs(channel1.get(endSample)) < threshold)) endSample -= 1
					println(s"endSample = $endSample")

					val segmentInterval = Interval[TimeSeconds](
						begin = TimeSeconds(beginSample / recording.sampleRate),
						end = TimeSeconds(endSample / recording.sampleRate)
					)
					println(s"segmentInterval = $segmentInterval")
					val segmentBuffer = subBuffer(buffer = recording, startFrame = beginSample, endFrame = endSample)
					println(s"segmentBuffer.duration = ${segmentBuffer.duration}")
					RecordingSegment(segmentBuffer, segmentInterval)
					//RecordingSegment(recording, recordingRange)
				}
		}
	}
}